import { create } from 'zustand'

export type BridgeType = 'whatsapp' | 'telegram' | 'signal' | 'discord' | 'slack' | null

export interface RoomSummary {
  roomId: string
  name: string
  avatarUrl: string | null
  topic: string
  isEncrypted: boolean
  isDirect: boolean
  bridgeType: BridgeType
  lastEvent: {
    body: string
    msgtype: string
    type?: string
    sender: string
    timestamp: number
  } | null
  unreadCount: number
  highlightCount: number
  /** True when there are unread messages even if they don't notify (muted /
   *  notify-off rooms report unreadCount 0 but should still show an indicator). */
  hasUnread: boolean
  membership: 'join' | 'invite' | 'leave'
}

interface RoomState {
  rooms: Map<string, RoomSummary>
  activeRoomId: string | null
  filter: string

  setActiveRoom: (roomId: string | null) => void
  setFilter: (text: string) => void
  upsertRoom: (roomId: string, data: Partial<RoomSummary>) => void
  removeRoom: (roomId: string) => void
  getSortedRooms: () => RoomSummary[]
}

export const useRoomStore = create<RoomState>((set, get) => ({
  rooms: new Map(),
  activeRoomId: null,
  filter: '',

  setActiveRoom: (roomId) => set({ activeRoomId: roomId }),
  setFilter: (text) => set({ filter: text }),

  upsertRoom: (roomId, data) =>
    set((state) => {
      const rooms = new Map(state.rooms)
      const existing = rooms.get(roomId)
      rooms.set(roomId, {
        roomId,
        name: '',
        avatarUrl: null,
        topic: '',
        isEncrypted: false,
        isDirect: false,
        bridgeType: null,
        lastEvent: null,
        unreadCount: 0,
        highlightCount: 0,
        hasUnread: false,
        membership: 'join',
        ...existing,
        ...data,
      })
      return { rooms }
    }),

  removeRoom: (roomId) =>
    set((state) => {
      const rooms = new Map(state.rooms)
      rooms.delete(roomId)
      return { rooms }
    }),

  getSortedRooms: () => {
    const { rooms, filter } = get()
    let list = Array.from(rooms.values()).filter(
      (r) => r.membership === 'join'
    )
    if (filter) {
      const lower = filter.toLowerCase()
      list = list.filter((r) => r.name.toLowerCase().includes(lower))
    }
    list.sort((a, b) => {
      const aTime = a.lastEvent?.timestamp ?? 0
      const bTime = b.lastEvent?.timestamp ?? 0
      return bTime - aTime
    })
    return list
  },
}))
